/* eslint-disable func-style */
import {isValid} from 'date-fns/isValid';
import {parse} from 'date-fns/parse';
import {parseISO as baseParseISO} from 'date-fns/parseISO';
import {startOfDay} from 'date-fns/startOfDay';

import {formatDate} from '@edna/utils';

const combineDateAndTime = (date?: Date | null, time?: string | null): Date => {
  if (!date) {
    return new Date();
  }

  const roundDate = startOfDay(date);
  const [hh = 0, mm = 0, ss = 0] = (time ?? '')
    .split(':')
    .map(Number)
    .filter((timePart) => !isNaN(timePart));

  return new Date(roundDate.getFullYear(), roundDate.getMonth(), roundDate.getDate(), hh, mm, ss);
};

const convertTimezoneToGMTOffset = (timezoneRegion: string) => {
  const now = new Date();

  const offsetString = new Intl.DateTimeFormat('en-US', {
    timeZone: timezoneRegion,
    timeStyle: 'long',
  }).formatToParts(now);

  return offsetString[8].value.replace('GMT', 'UTC');
};

const isDateBetween = (
  start: Date | string,
  end: Date | string = new Date(),
  current: Date | string = new Date(),
) => {
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();
  const currentTime = new Date(current).getTime();

  return currentTime >= startTime && currentTime <= endTime;
};

const getFormatTimeBySeconds = (sec: number) =>
  [
    Math.floor(sec / 60)
      .toString()
      .padStart(2, '0'),
    Math.floor(sec % 60)
      .toString()
      .padStart(2, '0'),
  ].join(':');

const parseDate = (payload: unknown, format: string, referenceDate: Date = new Date()) => {
  if (!payload || typeof payload !== 'string') {
    return '';
  }

  return parse(payload, format, referenceDate);
};

enum EIsoFormat {
  DATE = 'DATE',
  DATE_HM = 'DATE_HM',
  DATE_HMS = 'DATE_HMS',
}

const formats = {
  [EIsoFormat.DATE]: 'dd.MM.yyyy',
  [EIsoFormat.DATE_HM]: 'dd.MM.yyyy HH:mm',
  [EIsoFormat.DATE_HMS]: 'dd.MM.yyyy HH:mm:ss',
};

function parseISO(payload: unknown): Date | string;
function parseISO(payload: unknown, format?: keyof typeof EIsoFormat | string): string;

function parseISO(payload: unknown, format?: keyof typeof EIsoFormat | string) {
  if (!payload) {
    return '';
  }

  try {
    const date =
      typeof payload === 'string' ? baseParseISO(payload) : new Date(payload as number | Date);

    if (!isValid(date)) {
      return '';
    }

    return format
      ? (formatDate(date, {format: formats[format as EIsoFormat] || format}) as string)
      : date;
  } catch (e) {
    return '';
  }
}

const getAbsoluteDateTime = (date: Date, showTimeZone?: boolean) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}T${hours}:${minutes}:${seconds}${showTimeZone ? 'Z' : ''}`;
};

const getSeconds = (date: Date) =>
  date.getHours() * 60 * 60 + date.getMinutes() * 60 + date.getSeconds();

export {
  convertTimezoneToGMTOffset,
  isDateBetween,
  combineDateAndTime,
  getFormatTimeBySeconds,
  parseDate,
  parseISO,
  getAbsoluteDateTime,
  getSeconds,
};
/* eslint-enable func-style */
